import { html, TemplateElement, defineElement } from '@webtides/element-js';
import BuyBoxEvents from '../BuyBoxEvents';
import { CtaState } from './ProductCtaConstants';
import { LinkType } from '../../../../../types/link';
import { ifDefined } from 'lit-html/directives/if-defined';

export default class ProductCta extends TemplateElement {
    properties() {
        return {
            product: {},
            selectedVariant: null,
            ctaClassFallback: '',
        };
    }

    events() {
        return {
            '[add-to-cart]': {
                click: () => {
                    this.dispatch(BuyBoxEvents.ADD_TO_CART, this.selectedVariant, true);
                },
            },
        };
    }

    getCTAStyle(style) {
        const styles = {
            0: 'btn btn-brand',
            1: 'btn btn-accent',
            2: 'btn btn-outline-dark',
            3: 'btn btn-outline-light',
            4: 'btn btn-link',
            5: 'btn btn-theme',
            6: 'btn btn-light',
            7: 'btn btn-dark',
            8: 'btn btn-success',
            9: 'btn btn-danger',
        };

        return `${typeof style !== 'undefined' ? styles[style] : this.ctaClassFallback} h-14 mt-2 truncate max-w-full`;
    }

    button(link, style, icon, disabled) {
        return html`
            <button
                class="${this.getCTAStyle(style)}"
                ?disabled=${disabled}
                ?add-to-cart="${this.ctaState === CtaState.ADD_TO_CART}"
            >
                <span>${link.label}</span>
                ${icon ? html`<svg-icon name=${icon}></svg-icon>` : ''}
            </button>
        `;
    }

    templateButton({ link, style, icon, disabled }) {
        return html`${this.button(link, style, icon, disabled)}`;
    }

    templateModal({ link, style, icon, disabled }, id = '') {
        return html`
            <modal-trigger target-modal="${id ? id : link.href}">
                ${this.button(link, style, icon, disabled)}
            </modal-trigger>
        `;
    }

    templateLink({ link, style, icon }) {
        return html`
            <a
                class="${this.getCTAStyle(style)}"
                href="${link.href}"
                target="${link.target}"
                rel="${ifDefined(link.rel)}"
                ?download="${link.type === LinkType.ASSET}"
            >
                <span>${link.label}</span>
                ${icon ? html`<svg-icon name=${icon}></svg-icon>` : ''}
            </a>
        `;
    }

    templateForm({ link, style, icon, disabled }) {
        return html`
            <form method="post" action="${link.href}" target="${link.target}">
                <input type="hidden" name="csrf" value="${this.csrfToken}" />
                <input type="hidden" name="intent" value="show" />
                <input type="hidden" name="sku" value="${this.selectedVariant.sku}" />
                <input type="hidden" name="productName" value="${this.product.name}" />

                ${this.button(link, style, icon, disabled)}
            </form>
        `;
    }

    get csrfToken() {
        return window.siteConfig().csrf;
    }

    get ctaState() {
        return this.selectedVariant?.ctaState;
    }

    get ctaConfig() {
        if (!this.product.hasOwnProperty('ctaStates')) return '';
        return this.product?.ctaStates[this.selectedVariant?.ctaState];
    }

    template() {
        if (!this.ctaConfig) return;

        const state = this.ctaState;
        const { link, disabled } = this.ctaConfig;

        if (state === CtaState.ADD_TO_CART && link?.type === LinkType.DEFAULT) {
            return this.templateButton(this.ctaConfig);
        } else if (state === CtaState.AVAILABILITY_ALERT && link.type === LinkType.INTERN) {
            return this.templateForm(this.ctaConfig);
        } else if (state === CtaState.APPOINTMENT && link.type === LinkType.INTERN) {
            return this.templateForm(this.ctaConfig);
        } else if (state === CtaState.DEALER_SEARCH && link.type === LinkType.DEFAULT) {
            return this.templateModal(this.ctaConfig, 'retailer-search-modal');
        } else if (link?.type === LinkType.MODAL) {
            return this.templateModal(this.ctaConfig);
        } else if (disabled) {
            return this.templateButton(this.ctaConfig);
        } else {
            return this.templateLink(this.ctaConfig);
        }
    }
}

defineElement('product-cta', ProductCta);
